import * as XLSX from 'xlsx';
import type { ExcelData, DataRow, ExcelField } from '@/types';
import { EXCEL_FIELDS, DEFAULT_DESCRIPTION, IMAGE_SEPARATOR, FIRST_IMAGE_FIELD } from '@/lib/constants';

// 规范化标签：去掉前导 * 和空白，用于兼容 "产品主编号" / "*产品主编号" 两种写法
function normalizeLabel(label: string): string {
    return label.replace(/^\*+/, '').trim();
}

// 所有已知字段的规范化 label 集合（用于判断第1行是否为表头）
const KNOWN_LABELS = new Set(EXCEL_FIELDS.map(f => normalizeLabel(f.label)));

// 判断某行是否为表头行（包含任意已知字段标签）
function isHeaderRow(row: unknown[]): boolean {
    if (!row || row.length === 0) return false;
    return row.some(cell => {
        const label = normalizeLabel(String(cell ?? ''));
        return KNOWN_LABELS.has(label);
    });
}

function formatDateTime(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function getImportCreatedAt(): string {
    return formatDateTime(new Date());
}

// 导入 Excel 文件 (兼容带说明行格式和妙手格式)
export async function importExcel(file: File): Promise<ExcelData> {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];

    // 读取所有行 (header: 1 表示返回数组的数组)
    const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as unknown[][];

    if (allRows.length < 2) {
        throw new Error('Excel 文件格式不正确，至少需要表头行和数据行');
    }

    let description: string;
    let headerRowIndex: number;
    let dataStartIndex: number;

    if (isHeaderRow(allRows[0])) {
        // 妙手格式：第1行即为表头，无说明行
        description = DEFAULT_DESCRIPTION;
        headerRowIndex = 0;
        dataStartIndex = 1;
    } else {
        // 原格式：第1行说明，第2行表头
        description = String(allRows[0]?.[0] ?? DEFAULT_DESCRIPTION);
        headerRowIndex = 1;
        dataStartIndex = 2;
    }

    // 表头行
    const headerRow = allRows[headerRowIndex] || [];
    const headers: ExcelField[] = [];
    const headerKeyMap: { colIndex: number; field: ExcelField }[] = [];

    headerRow.forEach((headerCell, colIndex) => {
        const label = String(headerCell ?? '').trim();
        if (!label) return;
        const normLabel = normalizeLabel(label);
        // 匹配预定义字段（兼容有无 * 前缀）
        const matchedField = EXCEL_FIELDS.find(f => normalizeLabel(f.label) === normLabel);
        if (matchedField) {
            headers.push(matchedField);
            headerKeyMap.push({ colIndex, field: matchedField });
        } else {
            // 未知字段，创建自定义字段
            const customField: ExcelField = {
                key: `custom_${colIndex}`,
                label,
                index: colIndex + 1,
                isImage: label.includes('图') || label.includes('图片'),
            };
            headers.push(customField);
            headerKeyMap.push({ colIndex, field: customField });
        }
    });

    // 创建时间以本次 Excel 导入时刻为唯一依据，同一次导入的所有行使用同一时间。
    const importedAt = getImportCreatedAt();

    // 数据行
    const rows: DataRow[] = [];
    for (let i = dataStartIndex; i < allRows.length; i++) {
        const row = allRows[i];
        if (!row || row.every(cell => cell === '' || cell === null || cell === undefined)) {
            continue; // 跳过空行
        }
        const dataRow: DataRow = {};
        headerKeyMap.forEach(({ colIndex, field }) => {
            const value = row[colIndex];
            dataRow[field.key] = value !== null && value !== undefined ? String(value) : '';
        });
        dataRow.createdAt = importedAt;
        // 派生首图字段：取产品主图第一个链接
        dataRow.firstImage = parseImageUrls(dataRow.mainImages ?? '')[0] ?? '';
        rows.push(dataRow);
    }

    return {
        description,
        headers,
        rows,
        fileName: file.name,
    };
}

// 导出选项
export interface ExportOptions {
    // 是否用 skuSpec1 + skuSpec2（加号拼接，skuSpec2 存在时）覆盖平台SKU
    overwritePlatformSpec?: boolean;
}

// 导出 Excel 文件 (排除虚拟字段，按原始字段顺序)
export function exportExcel(data: ExcelData, fileName?: string, options?: ExportOptions): void {
    const wb = XLSX.utils.book_new();

    // 仅导出非虚拟字段
    const exportFields = data.headers.filter(f => !f.isVirtual);

    // 是否需要将 skuSpec1+skuSpec2 覆盖到平台SKU
    const overwritePlatformSpec = options?.overwritePlatformSpec ?? false;
    const hasPlatformSku = exportFields.some(f => f.key === 'platformSku');

    // 构建 AOA (Array of Arrays)
    const aoa: unknown[][] = [];

    // 第1行：说明文本
    aoa.push([data.description]);

    // 第2行：表头
    aoa.push(exportFields.map(f => f.label));

    // 第3行起：数据
    data.rows.forEach(row => {
        const dataRow = exportFields.map(field => {
            let value = row[field.key] ?? '';
            if (overwritePlatformSpec && hasPlatformSku && field.key === 'platformSku') {
                const spec1 = String(row.skuSpec1 ?? '').trim();
                const spec2 = String(row.skuSpec2 ?? '').trim();
                // 仅当 skuSpec2 存在时，用加号拼接覆盖平台SKU
                if (spec2) {
                    value = [spec1, spec2].filter(Boolean).join('+');
                }
            }
            return value;
        });
        aoa.push(dataRow);
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // 合并第1行的单元格 (A1 到最后一列)
    if (exportFields.length > 1) {
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: exportFields.length - 1 } }];
    }

    // 设置列宽
    ws['!cols'] = exportFields.map(() => ({ wch: 20 }));

    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    const outputName = fileName || data.fileName || 'export.xlsx';
    XLSX.writeFile(wb, outputName);
}

// 解析图片链接字段为数组 (兼容中文逗号、英文逗号、换行符)
export function parseImageUrls(value: string): string[] {
    if (!value) return [];
    // data URL 的 Base64 头部本身包含一个逗号，不能把它误拆成两张图片。
    if (value.startsWith('data:')) {
        const dataUrlMatch = value.match(/^data:[^,]+,[^，\n\r]+/);
        if (dataUrlMatch) {
            const first = dataUrlMatch[0].trim();
            const rest = value.slice(dataUrlMatch[0].length);
            return [first, ...parseImageUrls(rest)];
        }
    }
    return value
        .split(/[，,\n\r]+/)
        .map(url => url.trim())
        .filter(url => url.length > 0);
}

// 将图片链接数组合并为字段值 (统一用中文逗号)
export function joinImageUrls(urls: string[]): string {
    return urls.filter(u => u && u.trim()).map(u => u.trim()).join(IMAGE_SEPARATOR);
}

// 构造 SKU 标识名称：优先拼接 SKU 规格1 / SKU 规格2，缺失时回退平台SKU / skuId / 行号
export function buildSkuName(row: Record<string, unknown> | undefined, index?: number): string {
    if (!row) return index != null ? `#${index + 1}` : '';
    const spec1 = String(row.skuSpec1 ?? '').trim();
    const spec2 = String(row.skuSpec2 ?? '').trim();
    const parts = [spec1, spec2].filter(Boolean);
    if (parts.length > 0) return parts.join(' / ');
    const platformSku = String(row.platformSku ?? '').trim();
    if (platformSku) return platformSku;
    const skuId = String(row.skuId ?? '').trim();
    if (skuId) return skuId;
    return index != null ? `#${index + 1}` : '';
}

// 从产品主图字段中取首图 (第一个链接)
export function getFirstImage(mainImages: string): string {
    return parseImageUrls(mainImages)[0] ?? '';
}

// 将新首图写回产品主图字段 (替换第一个链接，保留其余)
export function setFirstImage(mainImages: string, newFirst: string): string {
    const urls = parseImageUrls(mainImages);
    if (urls.length === 0) {
        return newFirst;
    }
    urls[0] = newFirst;
    return joinImageUrls(urls);
}
